// CMaaS TypeScript SDK — a thin, dependency-free typed client for the F20
// API (docs/QUICKSTART.md shows usage; the full contract is docs/openapi.json,
// generated from the same code that serves the API). Node 18+ / browsers
// (global fetch).

export interface CmaasClientOptions {
  baseUrl: string;
  /** Tenant/property API key: `cmaas_<key_id>_<secret>`. */
  apiKey: string;
  fetchImpl?: typeof fetch;
}

export interface Page<T> {
  data: T[];
  next_cursor: string | null;
}

export interface ApiErrorBody {
  error: { code: string; message: string; docs_url: string; request_id: string; details?: Record<string, unknown> };
}

export class CmaasApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    super(`${body.error.code}: ${body.error.message}`);
    this.name = 'CmaasApiError';
  }
  get code(): string {
    return this.body.error.code;
  }
  get requestId(): string {
    return this.body.error.request_id;
  }
}

export interface Property {
  id: string;
  name: string;
  apple_platform: 'legacy' | 'uap' | 'dual';
  timezone: string;
  country_code: string | null;
  status: string;
  contact_number: string | null;
  contact_website: string | null;
  created_at: string;
  updated_at: string;
}

/** Write-only: never the secret or the vault pointer, just enough to visually confirm which credential is live. */
export interface SecretFingerprint {
  last4: string;
  sha256_prefix: string;
}

export interface ProviderConfig {
  id: string;
  property_id: string;
  name: string;
  provider_type: 'salto' | 'dormakaba' | 'assa_abloy' | 'simulated';
  apple_role: 'credential_manager' | 'integrator';
  is_default: boolean;
  wallet_keys_capable: boolean;
  supports_suspend_resume: boolean;
  has_secret_ref: boolean;
  has_secondary_secret_ref: boolean;
  has_client_secret_ref: boolean;
  secret_fingerprint: SecretFingerprint | null;
  secondary_secret_fingerprint: SecretFingerprint | null;
  client_secret_fingerprint: SecretFingerprint | null;
  api_url: string | null;
  secondary_api_url: string | null;
  /** True when the lock server may be reached over plain http on a public host (no TLS on the vendor side). */
  allow_insecure_http: boolean;
  provider_version: string | null;
  ble_keys_capable: boolean;
  physical_encoding_capable: boolean;
  device_registration_capable: boolean;
  key_sharing_capable: boolean;
  readback_capable: boolean;
  room_checkout_capable: boolean;
  credential_status_push_capable: boolean;
  device_status_push_capable: boolean;
  /** Per-vendor non-secret connection settings; shape depends on provider_type. */
  connection: Record<string, unknown> | null;
  status: string;
}

export interface Reservation {
  id: string;
  property_id: string;
  external_ref: string;
  guest_display_name: string | null;
  arrival_at: string;
  departure_at: string;
  unit_labels: string[];
  status: 'reserved' | 'checked_in' | 'checked_out' | 'cancelled';
  pass_template_id: string | null;
}

export interface Credential {
  id: string;
  property_id: string;
  provider_config_id: string;
  status: 'pending' | 'active' | 'suspended' | 'revoked' | 'expired' | 'failed';
  valid_from: string;
  valid_until: string | null;
  reservation_ids?: string[];
  // Vendor's raw key-issuance response, exactly as received. Only present
  // once a vendor dispatch has succeeded (e.g. absent while pending/failed).
  provider_data?: Record<string, unknown>;
}

export interface Pass {
  id: string;
  pass_ref: string;
  property_id: string;
  credential_id: string | null;
  pass_template_id: string | null;
  is_multi_reservation: boolean;
  platform: 'apple_wallet' | 'google_wallet';
  apple_mode: 'legacy' | 'uap' | null;
  google_mode: 'vendor_delegated' | null;
  status: 'pending' | 'active' | 'suspended' | 'revoked' | 'expired';
}

export interface WebhookSubscription {
  id: string;
  property_id: string;
  endpoint_url: string;
  event_types: string[];
  status: string;
  /** Present ONLY on the create/rotate response — never retrievable again. */
  signing_secret?: string;
}

export interface DomainEvent {
  id: string;
  property_id: string;
  subject_type: 'pass' | 'credential' | 'reservation' | 'event';
  subject_id: string;
  event_type: string;
  payload: Record<string, unknown>;
  occurred_at: string;
}

type Query = Record<string, string | number | boolean | undefined>;

export class CmaasClient {
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: CmaasClientOptions) {
    this.fetchImpl = options.fetchImpl ?? fetch;
  }

  async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; query?: Query; idempotencyKey?: string } = {},
  ): Promise<T> {
    const url = new URL(path, this.options.baseUrl);
    for (const [key, value] of Object.entries(opts.query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
    const res = await this.fetchImpl(url, {
      method,
      headers: {
        authorization: `Bearer ${this.options.apiKey}`,
        ...(opts.body !== undefined && { 'content-type': 'application/json' }),
        ...(opts.idempotencyKey && { 'idempotency-key': opts.idempotencyKey }),
      },
      ...(opts.body !== undefined && { body: JSON.stringify(opts.body) }),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;
    if (!res.ok) throw new CmaasApiError(res.status, body as ApiErrorBody);
    return body as T;
  }

  // --- Properties -----------------------------------------------------------
  createProperty(input: { name: string; apple_platform: string; timezone: string; country_code?: string }) {
    return this.request<Property>('POST', '/v1/properties', { body: input });
  }
  listProperties(query?: Query) {
    return this.request<Page<Property>>('GET', '/v1/properties', { query });
  }
  getProperty(id: string) {
    return this.request<Property>('GET', `/v1/properties/${id}`);
  }
  putWalletSettings(propertyId: string, input: Record<string, unknown>) {
    return this.request<Record<string, unknown>>('PUT', `/v1/properties/${propertyId}/wallet-settings`, { body: input });
  }
  createProviderConfig(propertyId: string, input: Record<string, unknown>) {
    return this.request<ProviderConfig>('POST', `/v1/properties/${propertyId}/provider-configs`, { body: input });
  }

  // --- Reservations -----------------------------------------------------------
  createReservation(input: Record<string, unknown>) {
    return this.request<Reservation>('POST', '/v1/reservations', { body: input });
  }
  updateReservation(id: string, input: Record<string, unknown>) {
    return this.request<Reservation>('PATCH', `/v1/reservations/${id}`, { body: input });
  }
  listReservations(query?: Query) {
    return this.request<Page<Reservation>>('GET', '/v1/reservations', { query });
  }

  // --- Credentials --------------------------------------------------------------
  issueCredential(
    input: {
      property_id: string;
      provider_config_id?: string;
      valid_from: string;
      valid_until?: string;
      reservation_ids?: string[];
    },
    idempotencyKey?: string,
  ) {
    return this.request<Credential>('POST', '/v1/credentials', { body: input, idempotencyKey });
  }
  getCredential(id: string) {
    return this.request<Credential>('GET', `/v1/credentials/${id}`);
  }
  revokeCredential(id: string) {
    return this.request<{ id: string; status: string }>('DELETE', `/v1/credentials/${id}`);
  }

  // --- Passes ----------------------------------------------------------------------
  /** Declarative create-or-converge (API-DESIGN §3). */
  declarePass(passRef: string, input: Record<string, unknown>) {
    return this.request<Pass>('PUT', `/v1/passes/${encodeURIComponent(passRef)}`, { body: input });
  }
  getPass(id: string) {
    return this.request<Pass & { reservation_ids: string[]; resolved_pass_template_id: string | null }>(
      'GET',
      `/v1/passes/${id}`,
    );
  }
  passAction(id: string, action: 'suspend' | 'resume' | 'revoke' | 'update') {
    return this.request<{ id: string; action: string; status: string }>('POST', `/v1/passes/${id}/actions/${action}`);
  }
  bundleAction(passId: string, bundleId: string, action: 'suspend' | 'resume' | 'revoke') {
    return this.request<{ id: string; pass_id: string; action: string; status: string }>(
      'POST',
      `/v1/passes/${passId}/bundles/${bundleId}/actions/${action}`,
    );
  }

  // --- Webhooks ----------------------------------------------------------------------
  createWebhook(propertyId: string, input: { endpoint_url: string; event_types?: string[] }) {
    return this.request<WebhookSubscription>('POST', `/v1/properties/${propertyId}/webhooks`, { body: input });
  }

  // --- Domain events ---------------------------------------------------------------------
  listDomainEvents(query?: Query) {
    return this.request<Page<DomainEvent>>('GET', '/v1/domain-events', { query });
  }
}
